"use client";

import { useEffect, useRef } from "react";

export default function OurApproachSection() {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const els = ref?.current?.querySelectorAll(".reveal");
        if (!els) return;
        const observer = new IntersectionObserver(
            (entries) =>
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("visible");
                        observer.unobserve(e.target);
                    }
                }),
            { threshold: 0.1 }
        );
        els?.forEach((el) => observer?.observe(el));
        return () => observer?.disconnect();
    }, []);

    return (
        <section
            ref={ref}
            className="section-navy-mid"
            style={{ paddingTop: "80px", paddingBottom: "80px" }}
        >
            <div
                className="px-6 lg:px-12"
                style={{ maxWidth: "1280px", margin: "0 auto" }}
            >
                <div className="text-center mb-8 reveal reveal-delay-1">
                    <p className="type-caption mb-2" style={{ color: "var(--color-red)" }}>OUR APPROACH</p>
                    <h2
                        style={{
                            fontFamily: "var(--font-serif)",
                            fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
                            fontWeight: 400,
                            color: "var(--color-white)",
                            lineHeight: 1.15,
                            letterSpacing: "-0.015em",
                        }}
                    >
                        How We Operate
                    </h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6 reveal reveal-delay-2">
                {[
                    {
                    icon: (
                        <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--color-white)"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="shrink-0"
                        >
                        <path d="M12 3v18" />
                        <path d="m19 8 3 8a5 5 0 0 1-6 0zV7" />
                        <path d="M3 7h1a17 17 0 0 0 8-2 17 17 0 0 0 8 2h1" />
                        <path d="m5 8 3 8a5 5 0 0 1-6 0zV7" />
                        <path d="M7 21h10" />
                        </svg>
                    ),
                    title: "Structured commercial discipline",
                    },
                    {
                    icon: (
                        <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--color-white)"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="shrink-0"
                        >
                        <path d="M22 12h-4l-2 4l-2-8l-2 4l-2-4l-2 8l-2-4l-2 4h-4" />
                        </svg>
                    ),
                    title: "Operational readiness",
                    },
                    {
                    icon: (
                        <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--color-white)"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="shrink-0"
                        >
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        </svg>
                    ),
                    title: "Clear risk allocation",
                    },
                    {
                    icon: (
                        <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--color-white)"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="shrink-0"
                        >
                        <path d="M12 3v18" />
                        <path d="m19 8 3 8a5 5 0 0 1-6 0zV7" />
                        <path d="M3 7h1a17 17 0 0 0 8-2 17 17 0 0 0 8 2h1" />
                        <path d="m5 8 3 8a5 5 0 0 1-6 0zV7" />
                        <path d="M7 21h10" />
                        </svg>
                    ),
                    title: "ESG-aligned sourcing",
                    },
                ].map((stage) => (
                    <div key={stage.title} className="card p-6">
                    <div
                        className="flex items-center gap-4"
                        style={{ color: "var(--color-red)" }}
                    >
                        <div
                        className="icon-box"
                        style={{
                            background: "var(--color-navy)",
                            borderRadius: "4px",
                            width: "48px",
                            height: "48px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            color: "var(--color-red)", // controls SVG color
                        }}
                        >
                        {stage.icon}
                        </div>

                        <h4
                        style={{
                            fontFamily: "var(--font-serif)",
                            fontSize: "1.1875rem",
                            fontWeight: 400,
                            color: "var(--color-white)",
                            lineHeight: 1.2,
                        }}
                        >
                        {stage.title}
                        </h4>
                    </div>
                    </div>
                ))}
                </div>

                <div className="mt-12 text-center reveal reveal-delay-3">
                    <p
                        className="reveal reveal-delay-4"
                        style={{
                            fontFamily: "var(--font-sans)",
                            fontSize: "1.0385rem",
                            color: "rgba(255,255,255,0.6)",
                            lineHeight: "1.6",
                        }}
                    >
                        We operate with confidentiality, professionalism, and long-term partnership focus.
                    </p>
                </div>
            </div>
        </section>
    );
}